import { Search as SearchIcon, X as ClearIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from 'helpers/cn';

type SearchInputProps = {
  className?: string;
  handleSearch: (value: string) => void;
  value?: string;
  placeholder?: string;
};

/**
 * A search field. Full width on a phone, its natural width above `md`.
 *
 * It used to collapse to a magnifier button below `md` and drop the field in as
 * `absolute left-0 mt-4 w-full`, with an `h-24` reservation on the wrapper to make
 * room for it. That needed a positioned ancestor of exactly the width the field
 * should take, at all eleven call sites, with nothing to enforce it - so the field
 * landed wherever the nearest positioned ancestor happened to be, and it was the
 * most fragile thing on mobile.
 *
 * The collapse existed to save horizontal room in a toolbar. Toolbars stack on a
 * phone now and give search its own line, so it was solving a problem that no longer
 * exists, and a field you can see and type into beats a button that reveals one.
 *
 * The magnifier is decoration and stays on the left; clear is an action and sits on
 * the right where it can be hit. They used to share the left slot, which meant the
 * affordance under your thumb changed meaning depending on whether the field had
 * content.
 */
export function SearchInput(props: SearchInputProps) {
  const { className, handleSearch, value = '', placeholder = 'Search' } = props;

  return (
    <div className={cn('relative w-full md:w-fit md:max-w-lg', className)}>
      <span className="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2">
        <SearchIcon className="text-dim size-4" strokeWidth={1.5} />
      </span>

      <Input
        type="search"
        placeholder={placeholder}
        value={value}
        onChange={(ev) => handleSearch(ev.target.value)}
        className={cn('pl-9 text-body', value ? 'pr-9' : 'pr-3')}
      />

      {!!value && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => handleSearch('')}
          className="text-dim hover:text-foreground hover:bg-hover focus-visible:ring-ring absolute top-1/2 right-1.5 z-10 flex size-7 -translate-y-1/2 items-center justify-center rounded transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none"
        >
          <ClearIcon className="size-4" strokeWidth={1.5} />
        </button>
      )}
    </div>
  );
}
